import { stripCanonicalTrailingSlash } from "../../../../lib/copy/src/canonical-url.mjs";
/**
 * A path-bound server answer is the only public identity input. This module
 * never manufactures locale variants or canonical URLs from browser location.
 */
export interface ResolvedPublicMetadata {
  schemaVersion: number; pathname: string; requestUrl?: string; kind: string; status: number;
  canonical: string; robots: string; indexable: boolean; contentIdentity: string;
  alternates: Array<{ hreflang: string; url: string }>;
}
const records = new Map<string, ResolvedPublicMetadata>();
const pending = new Map<string, Promise<ResolvedPublicMetadata | null>>();
export function readResolvedPublicMetadata(pathname: string): ResolvedPublicMetadata | null {
  if (typeof document === "undefined") return null;
  const key = pathname + window.location.search;
  if (records.has(key)) return records.get(key)!;
  const node = document.getElementById("public-route-decision");
  if (!node?.textContent) return null;
  try {
    const value = JSON.parse(node.textContent);
    if (value.schemaVersion !== 1 || value.kind !== "page" || value.status !== 200 ||
        value.pathname !== pathname || value.requestUrl && value.requestUrl !== key ||
        !value.canonical || !Array.isArray(value.alternates)) return null;
    records.set(key, value);
    if (records.size > 32) records.delete(records.keys().next().value!);
    return value;
  } catch { return null; }
}
export async function loadResolvedPublicMetadata(pathname: string) {
  const current = readResolvedPublicMetadata(pathname);
  if (current) return current;
  const key = pathname + window.location.search;
  if (!pending.has(key)) pending.set(key, (async () => {
    const response = await fetch(key, { headers: { Accept: "text/html" }, redirect: "follow" });
    if (!response.ok) return null;
    const html = new DOMParser().parseFromString(await response.text(), "text/html");
    const node = html.getElementById("public-route-decision");
    if (!node?.textContent) return null;
    const value = JSON.parse(node.textContent) as ResolvedPublicMetadata;
    if (value.schemaVersion !== 1 || value.kind !== "page" || value.status !== 200 ||
        !value.canonical || !Array.isArray(value.alternates) ||
        new URL(response.url).pathname !== value.pathname) return null;
    records.set(key, value);
    if (records.size > 32) records.delete(records.keys().next().value!);
    return value;
  })().finally(() => pending.delete(key)));
  return pending.get(key)!;
}
export function projectedHreflang() {
  if (typeof window === "undefined") return [];
  return (readResolvedPublicMetadata(window.location.pathname)?.alternates || [])
    .map(member => ({ hreflang: member.hreflang, href: member.url }));
}
export function applyResolvedPublicMetadata(value: ResolvedPublicMetadata) {
  value = { ...value, canonical: stripCanonicalTrailingSlash(value.canonical) };
  for (const node of document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang], meta[name="robots"], meta[property="og:url"]'))
    node.remove();
  const canonical = document.createElement("link");
  canonical.rel = "canonical"; canonical.href = value.canonical; document.head.append(canonical);
  for (const [name, content, property] of [["robots", value.robots, false], ["og:url", value.canonical, true]] as const) {
    const meta = document.createElement("meta");
    meta.setAttribute(property ? "property" : "name", name); meta.content = content; document.head.append(meta);
  }
  for (const member of value.alternates) {
    const link = document.createElement("link");
    link.rel = "alternate"; link.hreflang = member.hreflang; link.href = member.url;
    link.dataset.hreflang = "true"; document.head.append(link);
  }
}
